import { Injectable, inject } from '@angular/core';
import _ from 'lodash';

import { TODO } from '@b2b/types/core/utils';

import { CalendarService } from '../calendar/calendar.service';
import { HelpersService } from '../helpers/helpers.service';
import { FILTER_PORT, type IFilterPort } from '../utils/filter.port';
import { ROOT_SCOPE_PORT, type IRootScopePort } from '../utils/root-scope.port';
import { FlexpayService } from './flexpay/flexpay.service';
import { TemplatesHelpersService } from './helpers/templates-helpers.service';

/**
 * TemplatesService — Angular 18.
 *
 * Ported 1:1 from apps/b2b-ui/common/services/templates/templates.service.js
 * (legacy AngularJS name 'TemplatesService', module
 * 'acme.common.service.templates').
 *
 * Owns the in-progress event-template model (`model`) and all of the
 * schedule/price-list/add-on/membership accessors that the template editor
 * views drive. Delegates flexpay schedules to FlexpayService and shared
 * constants/formatting to TemplatesHelpersService.
 *
 * `$filter('date')` → FILTER_PORT, `$rootScope.$broadcast` → ROOT_SCOPE_PORT,
 * `angular.copy` → lodash `cloneDeep` (deep clone of plain model data).
 *
 * Registered back into AngularJS under 'TemplatesService' via
 * downgradeInjectable in app/upgrade/ng-downgraded-components.ts.
 */
@Injectable({ providedIn: 'root' })
export class TemplatesService {
  private $filter = inject<IFilterPort>(FILTER_PORT);
  private rootScope = inject<IRootScopePort>(ROOT_SCOPE_PORT);
  private calendar = inject(CalendarService);
  private helpers = inject(HelpersService);
  private templatesHelpers = inject(TemplatesHelpersService);
  private flexpayService = inject(FlexpayService);

  // Set via resetModel()
  model: TODO = {};

  isLoaded = false;

  tabName = '';
  isTabLoading = false;

  admissionTypes: TODO[] = [
    { label: 'Standard', name: 'standard' },
    { label: 'General Admission', name: 'generalAdmission' },
  ];

  isRepeating = false;

  restrict: TODO = {
    members: false,
  };

  repeatFrequency: TODO = {};

  repeatTypes = this.templatesHelpers.getRepeatTypes();
  repeatMonthTypes = this.templatesHelpers.getRepeatMonthTypes();
  salesChannels = this.templatesHelpers.getSalesChannels();
  createHour = this.templatesHelpers.createHour;
  createRange = this.templatesHelpers.createRange;
  hourToDate = this.templatesHelpers.hourToDate;

  flexpay = this.flexpayService;

  init(template?: TODO): void {
    if (template) {
      this.setModel(template);
    } else {
      this.resetModel();
    }
  }

  setIsLoaded(loaded: boolean): void {
    this.isLoaded = loaded;
  }

  setIsTabLoading(loading: boolean, tabName: TODO): void {
    this.isTabLoading = loading;
    this.tabName = tabName;
  }

  setModel(model: TODO): void {
    this.model = model;
    this.flexpayService.init(this.model);
    this.verifyCustomFields();
    this.verifyScheduleRanges();
    this.rootScope.$broadcast('TemplatesService::Update');
  }

  getModel(savePrep?: boolean): TODO {
    // Seed ticket details in case it does not exist
    this.getTicketDetails();
    this.getColorCategory();
    if (savePrep) {
      this.verifyCustomFields();
      this.verifySchedules();
    }
    return this.model;
  }

  stripNew(): void {
    if (this.hasSchedules()) {
      this.model.schedules[this.getNewSchedule().index].NEW = null;
      delete this.model.schedules[this.getNewSchedule().index].NEW;
    }
  }

  resetModel(): void {
    this.setModel({
      name: '',
      summary: '',
      description: '',
      shortDescription: '',
      emailConfirmationMessage: '',
      emailCancellationMessage: '',
      admissionType: 'standard',
      reviewState: 'draft',
      GLCode: null,
      rebookingFee: 0,
      ticketDetails: {
        ticketDurationDays: 1,
        expireFromEventDate: true,
        ticketPrintLines: ['', '', '', ''],
        checkInWindow: {
          blockScan: false,
          minutesBefore: 30,
          minutesAfter: 15,
          autoRebook: false,
        },
        postStartPurchase: {
          allowLateTicketPurchase: false,
          minutesAfter: 0,
        },
        purchaseAvailability: {
          timeFrame: 'when',
        },
        doNotPrint: false,
      },
      colorCategory: {
        textColor: '#000000',
        backgroundColor: '#d1d1d1',
      },
    });
  }

  hasSchedules(): boolean {
    return this.model.schedules && this.model.schedules instanceof Array && this.model.schedules.length > 0;
  }

  getSchedulesList(): TODO[] {
    let schedules: TODO[] = [];

    if (this.hasSchedules()) {
      schedules = this.processSchedules('standard');
    }
    return schedules;
  }

  getPrivateSchedulesList(): TODO[] {
    let schedules: TODO[] = [];

    if (this.hasSchedules()) {
      schedules = this.processSchedules('private');
    }
    return schedules;
  }

  removePrivateSchedule(): void {
    const schedules: TODO[] = [];
    for (let i = 0, ii = this.model.schedules.length; i < ii; i++) {
      if (this.model.schedules[i].type !== 'private') {
        schedules.push(this.model.schedules[i]);
      }
    }
    this.model.schedules = schedules;
  }

  createSchedule(scheduleStartTime?: TODO): TODO {
    const hour = this.createHour();
    const range = this.createRange();
    const sDate = scheduleStartTime ? new Date(scheduleStartTime) : this.hourToDate(hour);
    const startTime = sDate.setHours(12);
    const endTime = sDate.setHours(13);

    return {
      name: '',
      description: '',
      effectiveStartTime: this.calendar.toISO8601(startTime),
      effectiveEndTime: this.calendar.toISO8601(endTime),
      capacity: 0,
      duration: {
        hour: 1,
        minutes: 0,
      },
      repeatCycle: 'never',
      weekdays: [],
      hours: [hour],
      ranges: [range],
      monthly: [],
      daysOfMonth: [],
      days: [this.calendar.toYMDDate(sDate)],
      salesChannels: _.cloneDeep(this.salesChannels),
      orderForms: [],
      type: '',
    };
  }

  processScheduleDuration(schedule: TODO): string {
    let r = '';

    if (schedule.duration) {
      if (schedule.duration.hour > 0) {
        r += schedule.duration.hour + 'hr ';
      }
      if (schedule.duration.minutes > 0) {
        r += schedule.duration.minutes + 'min';
      }
    }

    return r;
  }

  orderSalesChannels(salesChannels: TODO): TODO {
    const channels: TODO[] = [];
    const _channels: TODO[] = [];
    const order = ['online', 'pointOfSale', 'reseller', 'customerRep', 'manualEntry', 'kiosk'];

    if (salesChannels instanceof Array) {
      for (let i = 0, ii = salesChannels.length; i < ii; i++) {
        if (order.length && order[0] == salesChannels[i].channel) {
          channels.push(salesChannels[i]);
          order.shift();
        } else {
          _channels.push(salesChannels[i]);
        }
      }

      salesChannels = channels.concat(_channels);
    }

    return salesChannels;
  }

  /**
   * Order price list according to expected values
   */
  processSalesChannels(priceList: TODO, restrict: TODO): TODO {
    // `restrict` mirrors the legacy signature; assigned to `order` but unused
    // downstream — preserved for a 1:1 port.
    let order: TODO;

    if (restrict instanceof Array) {
      order = restrict;
    }

    if (priceList && priceList.prices instanceof Array) {
      for (let i = 0, ii = priceList.prices.length; i < ii; i++) {
        if (priceList.prices[i].salesChannels instanceof Array) {
          priceList.prices[i].salesChannels = this.orderSalesChannels(priceList.prices[i].salesChannels);
        }
      }
    }

    return Object.keys(priceList).length ? priceList : null;
  }

  processSchedules(type: TODO): TODO[] {
    let effectiveDates: TODO;
    let priceList: TODO;
    const schedules: TODO[] = [];
    let startTime: TODO;
    let endTime: TODO;
    let range: TODO;
    let timesCount: TODO;

    for (let i = 0, ii = this.model.schedules.length; i < ii; i++) {
      const schedule = this.model.schedules[i];
      // Legacy used moment(...).add(1, 'year').format() as a display-only fallback
      // when a schedule has no effectiveEndTime. Replaced with native Date + the
      // migrated CalendarService to avoid a cross-package moment dependency; the
      // result only feeds getEffectiveDates() for a short-date label.
      const endDate =
        schedule.effectiveEndTime || this.addOneYearISO(this.model.lastPublished || schedule.effectiveStartTime);

      effectiveDates = this.templatesHelpers.getEffectiveDates(schedule.effectiveStartTime, endDate);
      range = 0;

      if (this.hasSchedulePriceLists(schedule)) {
        priceList = schedule.priceLists[0].name;
      } else if (this.hasPriceLists()) {
        priceList = this.getPriceList().name;
      } else {
        priceList = 'No price list';
      }

      if (this.isAdmissionType('generalAdmission')) {
        startTime = this.$filter('date')(
          this.hourToDate(schedule.ranges[0].start),
          this.templatesHelpers.getTimeFormat(),
        );
        endTime = this.$filter('date')(this.hourToDate(schedule.ranges[0].end), this.templatesHelpers.getTimeFormat());
      } else {
        startTime = this.$filter('date')(this.hourToDate(schedule.hours[0]), this.templatesHelpers.getTimeFormat());
        if (schedule.ranges instanceof Array && schedule.ranges.length) {
          endTime = this.$filter('date')(
            this.hourToDate(schedule.ranges[0].end),
            this.templatesHelpers.getTimeFormat(),
          );
        }
      }

      timesCount = this.getTimesCount(schedule);
      if (schedule.type === type) {
        schedules.push({
          id: schedule.id,
          name: schedule.name,
          capacity: schedule.capacity,
          startTime: startTime,
          endTime: endTime,
          timesCount: timesCount,
          duration: this.processScheduleDuration(schedule),
          repeatCycle: this.templatesHelpers.getRepeatType(this.repeatTypes, schedule.repeatCycle).label,
          resourceRequestTypes: this.templatesHelpers.getResourceRequestTypes(schedule.resourceRequests),
          orderForms: schedule.orderForms,
          salesChannels: this.getSalesChannels(schedule.salesChannels),
          effectiveDates: effectiveDates,
          priceList: priceList,
        });
      }
    }

    return schedules;
  }

  getTimesCount(model: TODO): TODO {
    return {
      general: this.createTimesCount(model.ranges ? model.ranges.length : 0),
      standard: this.createTimesCount(model.hours ? model.hours.length : 0),
    };
  }

  createTimesCount(range: number): string {
    return range > 1 ? '(' + '+' + (range - 1) + ')' : '';
  }

  getSalesChannels(channels: TODO): string {
    const _channels: TODO[] = [];

    for (let i = 0, ii = channels.length; i < ii; i++) {
      if (channels[i].enabled) {
        _channels.push(channels[i].name);
      }
    }

    return _channels.length === channels.length ? 'All' : _channels.join(', ');
  }

  isAdmissionType(type: TODO): boolean {
    return this.model.admissionType === type;
  }

  createTicketDetails(): TODO {
    return {
      ticketDurationDays: 1,
      expireFromEventDate: true,
      ticketPrintLines: ['', '', '', ''],
      checkInWindow: {
        blockScan: false,
        minutesBefore: 30,
        minutesAfter: 15,
        autoRebook: false,
      },
      postStartPurchase: {
        allowLateTicketPurchase: false,
        minutesAfter: 0,
      },
      doNotPrint: false,
    };
  }

  getTicketDetails(): TODO {
    if (!this.model.ticketDetails) {
      this.setTicketDetails(this.createTicketDetails());
    }
    return this.model.ticketDetails;
  }

  setResources(resources: TODO): void {
    this.model.resources = resources;
  }

  getResources(): TODO {
    if (!this.model.resources) {
      this.model.resources = [];
    }
    return this.model.resources;
  }

  setTicketDetails(details: TODO): void {
    this.model.ticketDetails = details;
  }

  getReviewState(): TODO {
    return this.model.reviewState;
  }

  setReviewState(state: TODO): void {
    this.model.reviewState = state;
  }

  setColorCategory(category: TODO): void {
    this.model.colorCategory = category;
  }

  getColorCategory(): TODO {
    if (!this.model.colorCategory || this.helpers.isEmptyObject(this.model.colorCategory)) {
      this.model.colorCategory = {
        textColor: '#000000',
        backgroundColor: '#d1d1d1',
      };
    }
    return this.model.colorCategory;
  }

  createMembership(): TODO {
    return {
      restrictions: [],
      discounts: [],
    };
  }

  setMembership(membership: TODO): void {
    this.model.membership = membership;
  }

  getMembership(): TODO {
    if (!this.model.membership) {
      this.setMembership(this.createMembership());
    }
    return this.model.membership;
  }

  hasMembershipRestrictions(): TODO {
    return this.model.memberOnlyEvent;
  }

  checkMembershipRestrictions(template: TODO): TODO {
    return template && template.memberOnlyEvent;
  }

  createEntitlementRule(): TODO {
    return {
      membershipLevels: { membershipLevelInfo: [], ruleApplies: 'forEachItem' },
      discountCodes: { discountCodeInfo: [], ruleApplies: 'forEachItem' },
      pricepoints: { pricepointInfo: [], ruleApplies: 'forEachItem' },
      quantity: 10000,
      timeFrame: {
        value: 1,
        period: 'day',
      },
    };
  }

  setMemberOnly(isMemberOnly: TODO): void {
    this.model.memberOnlyEvent = !!isMemberOnly;
  }

  setEntitlement(entitlementConfiguration: TODO): void {
    this.model.entitlementConfiguration = entitlementConfiguration;
  }

  setTicketsRules(ticketRuleConfig: TODO): void {
    if (this.model.entitlementConfiguration) {
      this.model.entitlementConfiguration.ticketRuleConfig = ticketRuleConfig;
    }
  }

  setSalesRules(salesRuleConfig: TODO): void {
    if (this.model.entitlementConfiguration) {
      this.model.entitlementConfiguration.salesRuleConfig = salesRuleConfig;
    }
  }

  getEntitlement(skipEntitlementRule?: TODO): TODO {
    if (!this.model.entitlementConfiguration) {
      this.setEntitlement({
        entitlementConfig: skipEntitlementRule ? [] : [this.createEntitlementRule()],
        salesRuleConfig: [],
        ticketRuleConfig: [],
      });
    }
    return this.model.entitlementConfiguration;
  }

  createPostStartPurchase(): TODO {
    return {
      allowLateTicketPurchase: false,
      minutesAfter: 0,
    };
  }

  getPostStartPurchase(): TODO {
    if (!this.model.ticketDetails.postStartPurchase) {
      this.model.ticketDetails.postStartPurchase = this.createPostStartPurchase();
    }
    return this.model.ticketDetails.postStartPurchase;
  }

  setPostStartPurchase(postStartPurchase: TODO): void {
    this.model.ticketDetails.postStartPurchase = postStartPurchase;
  }

  createAddOns(): TODO {
    return {
      inventoryAddOns: [],
      inventoryIds: [],
    };
  }

  hasAddOns(): TODO {
    return this.model.addOns && this.model.addOns.inventoryAddOns && this.model.addOns.inventoryAddOns.length > 0;
  }

  setAddOns(addOns: TODO): void {
    this.model.addOns = this.createAddOns();

    for (let i = 0, ii = addOns.length; i < ii; i++) {
      this.model.addOns.inventoryIds.push(_.cloneDeep(addOns[i].id));
      this.model.addOns.inventoryAddOns.push(_.cloneDeep(addOns[i]));
    }
  }

  getAddOns(): TODO {
    if (!this.hasAddOns()) {
      this.model.addOns = this.createAddOns();
    }
    return this.model.addOns.inventoryAddOns;
  }

  hasPriceLists(): TODO {
    return this.model.priceLists instanceof Array && this.model.priceLists[0];
  }

  getPriceList(): TODO {
    return this.hasPriceLists() ? this.model.priceLists[0] : {};
  }

  setPriceList(priceList: TODO): void {
    // Only set the first price list since we only
    // support a single price list on B2C and POS
    this.model.priceLists = [priceList];
  }

  hasSchedulePriceLists(schedule: TODO): boolean {
    return schedule && schedule.priceLists instanceof Array && schedule.priceLists.length > 0;
  }

  getSchedulePriceList(schedule: TODO): TODO {
    let r: TODO = false;

    if (this.hasSchedulePriceLists(schedule)) {
      r = schedule.priceLists[0];
    } else {
      r = _.cloneDeep(this.getPriceList());
    }

    return r;
  }

  setSchedulePriceList(schedule: TODO, priceList: TODO): void {
    schedule.priceLists = [priceList];
  }

  clearSchedulePriceList(schedule: TODO): void {
    if (this.hasSchedulePriceLists(schedule)) {
      schedule.priceLists = null;
      delete schedule.priceLists;
    }
  }

  hasScheduleResourceRequests(schedule: TODO): boolean {
    return schedule && schedule.resourceRequests instanceof Array && schedule.resourceRequests.length > 0;
  }

  getScheduleResourceRequests(schedule: TODO): TODO {
    let r: TODO = false;

    if (this.hasScheduleResourceRequests(schedule)) {
      r = schedule.resourceRequests;
    } else {
      r = [];
    }

    return r;
  }

  setScheduleResourceRequests(schedule: TODO, resourceRequests: TODO): void {
    schedule.resourceRequests = resourceRequests;
  }

  clearScheduleResourceRequests(schedule: TODO): void {
    if (this.hasScheduleResourceRequests(schedule)) {
      schedule.resourceRequests = null;
      delete schedule.resourceRequests;
    }
  }

  hasScheduleOrderForms(schedule: TODO): boolean {
    return schedule && schedule.orderForms instanceof Array && schedule.orderForms.length > 0;
  }

  getScheduleOrderForms(schedule: TODO): TODO {
    let r: TODO = false;

    if (this.hasScheduleOrderForms(schedule)) {
      r = schedule.orderForms;
    } else {
      r = [];
    }

    return r;
  }

  setScheduleOrderForms(schedule: TODO, orderForms: TODO): void {
    schedule.orderForms = orderForms;
  }

  clearScheduleOrderForms(schedule: TODO): void {
    if (this.hasScheduleOrderForms(schedule)) {
      schedule.orderForms = null;
      delete schedule.orderForms;
    }
  }

  hasScheduleTicketConfig(schedule: TODO): TODO {
    return schedule && schedule.ticketConfig && schedule.ticketConfig.id;
  }

  getScheduleTicketConfig(schedule: TODO): TODO {
    let r: TODO = false;

    if (this.hasScheduleTicketConfig(schedule)) {
      r = schedule.ticketConfig;
    } else {
      r = [];
    }

    return r;
  }

  setScheduleTicketConfig(schedule: TODO, ticketConfig: TODO): void {
    schedule.ticketConfig = ticketConfig;
  }

  clearScheduleTicketConfig(schedule: TODO): void {
    if (this.hasScheduleTicketConfig(schedule)) {
      schedule.ticketConfig = null;
      delete schedule.ticketConfig;
    }
  }

  setSchedule(schedule: TODO): void {
    if (schedule && schedule.id) {
      this.model.schedules[this.getScheduleById(schedule.id).index] = schedule;
    } else if (schedule.hasOwnProperty('NEW')) {
      this.model.schedules[this.getNewSchedule().index] = schedule;
    } else {
      this.addSchedule(schedule);
    }
  }

  getSchedule(index: TODO): TODO {
    let r: TODO = false;

    if (this.hasSchedules()) {
      r = this.model.schedules[index];
    }

    return r;
  }

  addSchedule(schedule: TODO): TODO {
    // All new schedules are unshifted to the array with a NEW property
    schedule.NEW = true;
    if (!this.model.schedules || !(this.model.schedules instanceof Array)) {
      this.model.schedules = [];
    }
    this.model.schedules.unshift(schedule);
    return schedule;
  }

  removeSchedule(index: TODO): void {
    if (this.hasSchedules()) {
      this.model.schedules.splice(index, 1);
    }
  }

  getNewSchedule(): TODO {
    let r: TODO = false;

    if (this.hasSchedules()) {
      for (let i = 0, ii = this.model.schedules.length; i < ii; i++) {
        if (this.model.schedules[i].hasOwnProperty('NEW')) {
          r = {
            index: i,
            schedule: this.model.schedules[i],
          };
          break;
        }
      }
    }

    if (!r) {
      // New schedule is unshifted so index is 0
      r = {};
      r.index = 0;
      r.schedule = this.addSchedule(this.createSchedule());
    }

    return r;
  }

  removeNewSchedule(): void {
    this.model.schedules.splice(this.getNewSchedule().index, 1);
  }

  getScheduleById(id: TODO): TODO {
    let r: TODO = false;

    if (id && this.hasSchedules()) {
      for (let i = 0, ii = this.model.schedules.length; i < ii; i++) {
        if (this.model.schedules[i].id === id) {
          r = {
            index: i,
            schedule: this.model.schedules[i],
          };
          break;
        }
      }
    }

    return r;
  }

  setScheduleById(id: TODO, schedule: TODO): void {
    const found = this.getScheduleById(id);
    if (found) {
      this.model.schedules[found.index] = schedule;
    }
  }

  removeScheduleById(id: TODO): void {
    this.model.schedules.splice(this.getScheduleById(id).index, 1);
  }

  getScheduleHours(schedule: TODO): TODO[] {
    const hours: TODO[] = [];

    if (schedule && schedule.hours) {
      for (let i = 0, ii = schedule.hours.length; i < ii; i++) {
        hours.push(this.hourToDate(schedule.hours[i]));
      }
    } else {
      hours.push(this.hourToDate(this.createHour()));
    }

    return hours;
  }

  getScheduleRangeHours(schedule: TODO): TODO[] {
    let range: TODO;
    const ranges: TODO[] = [];

    if (schedule && schedule.ranges) {
      for (let i = 0, ii = schedule.ranges.length; i < ii; i++) {
        ranges.push({
          start: this.hourToDate(schedule.ranges[i].start),
          end: this.hourToDate(schedule.ranges[i].end),
        });
      }
    } else {
      range = this.createRange();
      ranges.push({
        start: this.hourToDate(range.start),
        end: this.hourToDate(range.end),
      });
    }

    return ranges;
  }

  getPrimaryPhoto(): TODO {
    if (this.model.images instanceof Array) {
      for (let i = 0, ii = this.model.images.length; i < ii; i++) {
        if (this.model.images[i].id === this.selectedPrimaryPhoto) {
          return this.model.images[i].screen;
        }
      }
    }
  }

  hasImages(): boolean {
    return this.model.images instanceof Array;
  }

  isRepeatCycle(name: TODO): boolean {
    return this.repeatFrequency.value === name;
  }

  getRepeatType(type: TODO): TODO {
    let r: TODO = false;

    for (let i = 0, ii = this.repeatTypes.length; i < ii; i++) {
      if (this.repeatTypes[i].value === type) {
        r = this.repeatTypes[i];
        break;
      }
    }

    return r;
  }

  getRepeatMonthType(type: TODO): TODO {
    let r: TODO = false;

    for (let i = 0, ii = this.repeatMonthTypes.length; i < ii; i++) {
      if (this.repeatMonthTypes[i].value === type) {
        r = this.repeatMonthTypes[i];
        break;
      }
    }

    return r;
  }

  getScheduleRepeatCycle(schedule: TODO): TODO {
    let r: TODO = this.repeatTypes[0];

    if (schedule) {
      for (let i = 0, ii = this.repeatTypes.length; i < ii; i++) {
        if (schedule.repeatCycle === this.repeatTypes[i].value) {
          r = this.repeatTypes[i];
          break;
        }
      }
    }

    return r;
  }

  setExpireFromEventDate(value: TODO): void {
    this.model.ticketDetails.expireFromEventDate = value;
  }

  setPurchaseAvailability(timeFrame: TODO, minutes: TODO): void {
    if (this.model.ticketDetails.purchaseAvailability) {
      this.model.ticketDetails.purchaseAvailability.timeFrame = timeFrame;
      this.model.ticketDetails.purchaseAvailability.minutes = minutes;
    } else {
      this.model.ticketDetails.purchaseAvailability = {
        timeFrame: timeFrame,
        minutes: minutes,
      };
    }
  }

  hasValidDiscounts(list: TODO, property: TODO): boolean {
    let r = true;
    const discountCategoryHash: TODO = {};
    const discountCategoryIds = list.map(function (discount: TODO) {
      if (discount[property] && discount[property].id) {
        return discount[property].id;
      } else {
        return '';
      }
    });

    for (let i = 0, ii = discountCategoryIds.length; i < ii; i++) {
      if (discountCategoryIds[i]) {
        if (discountCategoryHash[discountCategoryIds[i]]) {
          r = false;
          break;
        } else {
          discountCategoryHash[discountCategoryIds[i]] = 1;
        }
      }
    }

    return r;
  }

  hasDiscounts(list: TODO, property: TODO): boolean {
    let r = true;
    for (let i = 0, ii = list.length; i < ii; i++) {
      if (!list[i][property] || !list[i][property].id) {
        r = false;
        break;
      }
    }
    return r;
  }

  hasMembershipDiscount(list: TODO, property: TODO): boolean {
    let r = true;
    for (let i = 0, ii = list.length; i < ii; i++) {
      if (!list[i][property] || !list[i][property].length) {
        list[i]._errors = [true].concat(list[i]._errors);
        r = false;
      }
    }
    return r;
  }

  verifyCustomFields(): void {
    // Splice out custom fields with no values to prevent errors
    if (this.model.customFields instanceof Array) {
      for (let i = this.model.customFields.length - 1, ii = 0; i >= ii; i--) {
        if (
          this.model.customFields[i].value &&
          this.model.customFields[i].name.replace(/s+/g).length > 0 &&
          this.model.customFields[i].value.replace(/s+/g).length > 0
        ) {
          // Valid do nothing
        } else {
          // Splice out the fields
          this.model.customFields.splice(i, 1);
        }
      }
    }
  }

  verifySchedules(): void {
    if (this.hasSchedules()) {
      // Migrate start dates to all schedules
      for (let i = 0, ii = this.model.schedules.length; i < ii; i++) {
        // Copy temporary weekday values
        if (this.model.schedules[i].repeatCycle === 'month') {
          if (this.model.schedules[i].daysOfMonth instanceof Array && this.model.schedules[i].daysOfMonth.length > 0) {
            this.model.schedules[i].monthly = null;
            delete this.model.schedules[i].monthly;

            this.model.schedules[i].weekdays = null;
            delete this.model.schedules[i].weekdays;
          } else if (this.model.schedules[i].monthly instanceof Array && this.model.schedules[i].monthly.length > 0) {
            this.model.schedules[i].daysOfMonth = null;
            delete this.model.schedules[i].daysOfMonth;
          }
        } else {
          this.model.schedules[i].monthly = null;
          delete this.model.schedules[i].monthly;

          this.model.schedules[i].daysOfMonth = null;
          delete this.model.schedules[i].daysOfMonth;

          // Include a days object if event never repeats
          if (this.model.schedules[i].repeatCycle === 'never') {
            this.model.schedules[i].days = [
              this.calendar.toYMDDate(this.calendar.parseDate(this.model.schedules[i].effectiveStartTime)),
            ];
          } else {
            this.model.schedules[i].days = null;
            delete this.model.schedules[i].days;
          }
        }
      }

      this.verifyScheduleRanges();
    }
  }

  verifyScheduleRanges(): void {
    // if General Admission, make sure the schedule has a ranges property or create one.
    if (this.isAdmissionType('generalAdmission')) {
      for (let i = 0, ii = this.model.schedules.length; i < ii; i++) {
        if (!this.model.schedules[i].ranges) {
          this.model.schedules[i].ranges = [];
          this.model.schedules[i].ranges.push(this.createRange());
        }
      }
    }
  }

  // Set when a primary photo is selected in the template editor.
  selectedPrimaryPhoto?: TODO;

  /** Adds one year to a date and returns it as an ISO-8601 timestamp. */
  private addOneYearISO(date: TODO): TODO {
    const base = new Date(date);
    base.setFullYear(base.getFullYear() + 1);
    return this.calendar.toISO8601(base);
  }
}
